import React, { forwardRef, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import Apptentive from 'helpers/apptentive'
import SearchIcon from '@mui/icons-material/Search'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import ErrorMessage from 'components/ErrorMessage'

import { color } from 'styles/colors'

type Props = {
  onChange: (value: string) => void;
  onSearch: (value: string) => void;
  noForm?: boolean;
  ariaLabel: string;
  value?: string;
  placeholder?: string | undefined;
  helperText?: string | null;
  className?: string;
  showClearIcon?: boolean;
  focusOnMount?: boolean;
  variant?: string;
  hideSearchButton?: boolean;
  errorMessage?: string;
  onClickClearIcon?: () => void;
  showSpinner?: boolean;
}

const Search = forwardRef<HTMLInputElement, Props>(({
  onChange,
  onSearch,
  noForm,
  ariaLabel,
  value,
  placeholder,
  helperText,
  focusOnMount,
  className,
  variant,
  showClearIcon,
  onClickClearIcon,
  hideSearchButton,
  errorMessage,
  showSpinner = false,
  ...inputProps
}: Props, ref) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (focusOnMount) inputRef?.current?.focus()
  }, [focusOnMount])

  const clear = useCallback(() => {
    if (onClickClearIcon) {
      onClickClearIcon()
    } else { // TODO: should we always call onChange and onSearch even if we have onClickClearIcon?
      onChange('')
      onSearch('')
    }
    inputRef.current?.focus()
  }, [onChange, onSearch, onClickClearIcon])

  const onKeyUp = useCallback((event) => {
    if (event.key === 'Escape') {
      clear()
    }
  }, [clear])

  const onClickSubmit = useCallback((event) => {
    event.preventDefault()
    Apptentive.sendEvent('search_from_' + ariaLabel.replace(' ', '_').toLowerCase())
    onSearch(value || '')
  }, [onSearch, value, ariaLabel])
  const isSearchEnabled = !showSpinner && value
  const renderSearchInput = () => {
    return (
      <>
        <S.SearchContainer>
          <S.Input
          ref={ref || inputRef}
          type="text"
          placeholder={placeholder}
          aria-label="search"
          onChange={(event) => onChange(event.target.value)}
          styleVariant={variant}
          hideSearchButton={hideSearchButton}
          {...{ onKeyUp, value, ...inputProps }}
          />
          {(value || showClearIcon) && (
          <S.Clear
            role="button"
            aria-label="clear"
            className="icon-cancel-circle"
            onClick={clear}
            type="button"
          />
          )}

          {!hideSearchButton && (
            variant === 'text'
              ? (
                <Button
                decoration="positive"
                enabled={!!isSearchEnabled}
                styles={{
                  button: {
                    height: 39,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 5,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 5
                  }
                }}
                onPress={onClickSubmit}
                showSpinner={showSpinner}
                {...{ text: 'Search' }}
                />)
              : (
                <Button
                showSpinner={showSpinner}
                decoration="positive"
                enabled={!!isSearchEnabled}
                styles={{
                  button: {
                    height: 39,
                    minWidth: 25,
                    paddingRight: 8,
                    paddingLeft: 8,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 5,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 5
                  }
                }}
                onPress={onClickSubmit}
                >
                  <S.SearchIcon />
                </Button>
              )
          )
        }
        </S.SearchContainer>

        {helperText && <S.HelperText>{helperText}</S.HelperText>}
        {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
      </>
    )
  }

  if (noForm) {
    return renderSearchInput()
  } else {
    return (
      <form aria-label={ariaLabel} role="search" {...{ className }} onSubmit={onClickSubmit}>
        {renderSearchInput()}
      </form>
    )
  }
})

export default Search

const buttonWidth = (variant: string | undefined) => variant === 'icon' ? '39px' : '68px'

const S = {
  SearchContainer: styled.div`
    display: flex;
  `,
  Input: styled.input.withConfig({
    shouldForwardProp: (prop) => !['hideSearchButton', 'styleVariant'].includes(prop)
  })<{ styleVariant: string | undefined, hideSearchButton: boolean | undefined }>`
    height: 39px;
    width: calc(100% - ${({ styleVariant, hideSearchButton }) => hideSearchButton ? '0px' : buttonWidth(styleVariant)});
    display: inline-block;
    padding-right: 33px;

    font-size: 12pt;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;

    &::placeholder {
      color: ${color('searchPlaceholder')};
    }
  `,
  Clear: styled.button`
    color: ${color('clearIcon')};
    margin: auto 2px auto -34px;
    padding: 9px;
    height: 32px;
    width: 32px;

    &:hover {
      color: ${color('clearIconHover')};
    }
  `,
  HelperText: styled.div`
    margin-top: 3px;
  `,
  SearchIcon: styled(SearchIcon)`
    color: ${color('primaryButtonText')};
    font-size: 23px;
  `
}
